// 效果评估表格（ProTablePlusForm + addGroup）：
//  - 联动序号列（父行，数量派生自 online_details「预计联动周期」，随其增删实时调和）
//  - 效果指标 + 效果预期（addGroup 子表，可增删，custom-component 受控范式）
//  - 评估报告列（每个联动序号行一个附件上传 cell）
// 参考：SolutionTable（addGroup / treeAddToParent / treeDeleteRowById / validateFields 委托）

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import {
  ProTablePlusForm,
  type ProTablePlusFormRef,
} from '@mpc/common/src/components/ProTablePlusForm';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import { EffectMetricSelect, ExpectValueInput, useKpiOptions } from './cells';
import "./index.less"

/** 中文数字（第N次联动展示，覆盖到第十次） */
const CN_NUM = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

/** 效果评估表格行（扁平树形数据，addGroup 驱动；领域 key 对齐接口 IpCooperationEffectEvaluationGroupForm / IndicatorValueDto） */
export interface EffectEvaluationRow {
  /** 联动序号父行唯一标识（uuid，树节点 id；子行经树合并继承） */
  row_key?: string;
  /** 联动序号（第N次联动，父行携带，子行继承；对齐接口 link_seq，对应上线明细 index_id） */
  link_seq?: number;
  /** 同一联动内指标序号（对齐接口 index_id；addGroup 新增时计算） */
  index_id?: number;
  /** 效果指标子行唯一标识（uuid，addGroup 树节点内部 id，非接口字段） */
  metric_id?: string;
  /** 效果指标（指标编号 IKPI0001...） */
  kpi_code?: string;
  /** 效果预期 */
  expect_value?: string;
  /** 评估报告附件（父行；提交时映射接口 blob_ids） */
  assessment_attachs?: any[];
  /** addGroup 内部占位行标记 */
  $addRow?: string[];
}

/** SFormItem watchChildFormValidate 提交时调用的校验能力 */
export interface EffectEvaluationTableRef {
  validateFields: () => Promise<unknown>;
}

export interface EffectEvaluationTableProps {
  /** 受控表格数据（表单 effect_evaluation 字段值） */
  value?: EffectEvaluationRow[];
  /** 数据变更回调 */
  onChange?: (next: EffectEvaluationRow[]) => void;
  /** 只读（详情纯展示） */
  view?: boolean;
  /** 联动序号行数 = 预计联动周期（online_details）数量 */
  onlineDetailsCount?: number;
}

/**
 * 调和联动序号父行数量到目标 count（保留既有父行及其指标子行，追加缺失/删除超量父行）。
 * 幂等：父行数量匹配返回 null（防死循环）。addGroup 占位行（$addRow）不参与父行计数。
 */
function reconcileLinkageRows(
  rows: EffectEvaluationRow[] | undefined,
  count: number
): EffectEvaluationRow[] | null {
  const list = rows ?? [];
  // 排除 addGroup 占位行（携带父行 key 但不属于真实指标子行）
  const realRows = list.filter((r) => !r.$addRow);
  // 父行按 link_seq 索引（任一子行继承 link_seq，取其 row_key）
  const parentByIndex = new Map<number, string>();
  realRows.forEach((r) => {
    if (r.row_key && r.link_seq != null && !parentByIndex.has(r.link_seq)) {
      parentByIndex.set(r.link_seq, r.row_key);
    }
  });
  if (parentByIndex.size === count) return null;
  // 保留 link_seq <= count 的父行及其子行
  const keptKeys = new Set(
    Array.from(parentByIndex.entries())
      .filter(([seq]) => seq <= count)
      .map(([, key]) => key)
  );
  const kept = realRows.filter((r) => keptKeys.has(r.row_key));
  // 追加缺失序号的父行
  const additions: EffectEvaluationRow[] = [];
  for (let i = 1; i <= count; i++) {
    if (parentByIndex.has(i)) continue;
    additions.push({
      row_key: uuidv4(),
      link_seq: i,
      assessment_attachs: [],
    });
  }
  return [...kept, ...additions];
}

// SFormItem watchChildFormValidate 会注入 ref 并调用 validateFields 联动校验（委托 ProTablePlusForm 范式）；
// forwardRef 把校验能力转发到内部 ProTablePlusForm，实现提交时：
//   1) 效果指标/效果预期列必填（cell 级 required 规则）
//   2) 每个联动序号至少一行指标（联动级校验，覆盖无指标行的联动）
const EffectEvaluationTable = forwardRef<
  EffectEvaluationTableRef,
  EffectEvaluationTableProps
>((props, ref) => {
  const tableRef = useRef<ProTablePlusFormRef>(null);
  const { value = [], onChange, view = false, onlineDetailsCount = 0 } = props;
  // 最新 props 引用：useImperativeHandle deps=[] 闭包捕获首帧 props，校验需读最新 value/onlineDetailsCount
  const propsRef = useRef(props);
  propsRef.current = props;
  useImperativeHandle(ref, () => ({
    validateFields: () => {
      const { value = [], onlineDetailsCount = 0 } = propsRef.current;
      // 1) 单元格必填校验（效果指标/效果预期；委托 ProTablePlusForm validateFields）
      const cellValidate = tableRef.current?.validateFieldsAndScroll
        ? tableRef.current.validateFieldsAndScroll()
        : Promise.resolve();
      return cellValidate
        .then(() => {
          // 2) 联动级校验：每个联动序号至少一行真实指标子行（非 $addRow 占位）
          const realRows = (value ?? []).filter(
            (r) => !r?.$addRow && r?.metric_id
          );
          const coveredSeqs = new Set<number>();
          realRows.forEach((r) => {
            if (r.link_seq != null) coveredSeqs.add(r.link_seq);
          });
          for (let seq = 1; seq <= onlineDetailsCount; seq++) {
            if (!coveredSeqs.has(seq)) {
              return Promise.reject(
                `第${CN_NUM[seq - 1] || seq}次联动请至少添加一行效果指标和效果预期`
              );
            }
          }
          return Promise.resolve();
        })
        .catch((e) => {
          return Promise.reject(e);
        });
    },
  }));

  const isEdit = !view;
  const kpiOptions = useKpiOptions();

  // 最新受控 value 引用：调和 effect 经 ref 读最新行，避免把 value 加入 columns 依赖导致每次编辑重建列
  const valueRef = useRef(value);
  valueRef.current = value;

  const columns = useMemo<AddGroupFormColumn[]>(
    () => [
      {
        dataIndex: 'link_seq',
        title: '联动序号',
        width: 100,
        mergeRowSpanKey: ['row_key'],
        render: (linkSeq: number) => {
          if (linkSeq == null) return <span className="text-color-3">--</span>;
          return <span>{`第${CN_NUM[linkSeq - 1] || linkSeq}次联动`}</span>;
        },
      },
      {
        // title:null → addGroup 子列平铺（不包父表头），对齐设计稿 5 平铺列
        title: '',
        // 新增指标按钮仅编辑态渲染（只读态无增删入口）
        addGroup: true,
        addGroupKey: ['row_key'],
        addCellRender: isEdit
          ? (r: any) => (
              <Button
                icon={<Plus />}
                type="checked"
                onClick={() => {
                  // 同一联动内指标序号 = 现有指标子行数 + 1（对齐接口 index_id）
                  const siblingCount = valueRef.current.filter(
                    (row) =>
                      !row.$addRow &&
                      row.metric_id &&
                      row.row_key === r?.row_key
                  ).length;
                  tableRef.current?.treeAddToParent(
                    'metric_id',
                    { metric_id: uuidv4(), index_id: siblingCount + 1 },
                    r?.row_key
                  );
                }}
              >
                新增
              </Button>
            )
          : null,

        children: [
          {
            dataIndex: 'index',
            title: '#',
            width: 0,
            mergeRowSpanKey: ['row_key', 'metric_id'],
            render: (v, r, index, ...rest) => {
              return <span>{(r?.l1$index ?? 0) + 1}</span>;
            },
          },
          {
            dataIndex: 'kpi_code',
            title: '效果指标',
            width: 320,
            mergeRowSpanKey: ['row_key', 'metric_id'],
            editRender: ({ row }: any) => ({
              name: 'custom-component',
              editable: isEdit,
              props: {
                customComp: EffectMetricSelect,
                options: kpiOptions,
              },
              rule: [{ required: true, message: '请填写效果指标' }],
            }),
          },
          {
            dataIndex: 'expect_value',
            title: '效果预期',
            width: 320,
            mergeRowSpanKey: ['row_key', 'metric_id'],
            editRender: ({ row }: any) => ({
              name: 'custom-component',
              editable: isEdit,
              props: {
                customComp: ExpectValueInput,
                editable: isEdit,
                options: kpiOptions,
                row,
              },
              rule: [{ required: true, message: '请填写效果预期' }],
            }),
          },
          // 删除指标子行按钮仅编辑态渲染
          ...(isEdit
            ? [
                {
                  dataIndex: 'operation',
                  title: '操作',
                  width: 80,
                  mergeRowSpanKey: ['row_key', 'metric_id'],
                  render: (__v: any, row: any) => {
                    // $addRow 占位行不渲染删除（其操作 cell 已被 addGroup colSpan 隐藏，此处防御）
                    if (row?.$addRow) return null;
                    return (
                      <Button
                        danger
                        type="text"
                        onClick={() => {
                          tableRef.current?.treeDeleteRowById(row?.metric_id);
                        }}
                      >
                        删除
                      </Button>
                    );
                  },
                },
              ]
            : []),
        ],
      },
      {
        dataIndex: 'kpi_schema',
        title: '指标说明',
        hideInTable: true,
      },
      {
        dataIndex: 'assessment_attachs',
        title: '评估报告',
        width: 220,
        mergeRowSpanKey: ['row_key'],
        editRender: {
          name: 'upload',
          editable: isEdit,
          props: {
            editable: isEdit,
            className:'custom-effect-evaluation-table-upload'
          },
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, kpiOptions]
  );

  // columns 最新引用（调和 effect 经 ref 读，避免把 columns 加入依赖触发重复调和）
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const handleDataChange = useMemoizedFn((newData) => {
    onChange?.(newData);
  });

  /**
   * 联动序号调和：onlineDetailsCount（预计联动周期行数）变化时同步父行数量。
   *  - 新建单据/详情编辑态：仅编辑态（isEdit）调和，只读态不改写表单数据
   *  - addGroup 树在外部 value 变化时不自动重建，须显式 treeInitData 重建
   */
  const reconcile = useMemoizedFn((count: number) => {
    const next = reconcileLinkageRows(valueRef.current, count);
    if (!next) return;
    // 先同步表单（treeInitData 内部 setData 走可控 onChange，显式 onChange 兜底保证表单值与树一致）
    setTimeout(() => {
      onChange?.(next);
      tableRef.current?.treeInitData?.(next);
    }, 10);
  });

  useEffect(() => {
    if (!isEdit || !tableRef.current) return; // 只读态不调和（纯展示，不改写表单）
    reconcile(onlineDetailsCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineDetailsCount, isEdit]);

  return (
    <ProTablePlusForm
      ref={tableRef}
      value={value}
      onDataChange={handleDataChange}
      columns={columns}
      rowKey={(r) => `${r.row_key}-${r.metric_id}`}
      verticalBorder
      scroll={{ x: 'max-content' }}
    />
  );
});

export default EffectEvaluationTable;
